<template>
  <MarketplaceCard
    v-if="config"
    v-model="switchOn"
    logo-alt="Broccoli AI"
    description="Broccoli AI answers your calls after hours and books appointments straight into your schedule."
    learn-more-link="https://refer.broccoli.com/sera-systems"
    cy-selector="marketplaceBroccoli"
  >
    <template #logo>
      <div class="w-full d-flex align-center h-12">
        <BroccoliLogo class="h-[26px] my-auto" />
      </div>
    </template>
    <template #configure>
      <div class="link" data-cy="broccoli-setup" @click="openModal">
        Set Up Information
      </div>
    </template>
    <template #modal>
      <Modal
        v-if="modalOpen"
        title="Broccoli AI Configuration"
        class-name="broccoli-config-modal"
        no-submit
        @hide-modal="closeModal"
      >
        <div class="d-flex flex-column">
          <template v-if="provisioningError">
            <Alert
              type="error"
              :text="`Broccoli AI provisioning failed: ${provisioningError}`"
              class="mb-3"
              data-cy="broccoli-provision-error"
            />
            <p class="mb-3">
              Something went wrong while provisioning Broccoli AI. Try again, or
              contact support if this keeps happening.
            </p>
          </template>
          <template v-else-if="revealedKey">
            <Alert
              type="success"
              text="Your API key for Broccoli AI has been provisioned. This is the only time we will show you this API key — copy it now and store it somewhere secure. If you lose it, use Regenerate to create a new one."
              class="mb-3"
              data-cy="broccoli-provision-success"
            />
            <InputField
              v-model="revealedKey"
              :disabled="true"
              title="API Key"
              placeholder="API Key"
            />
          </template>
          <template v-else-if="provisioning">
            <p
              class="text-gray text-center mb-3"
              data-cy="broccoli-provisioning"
            >
              Provisioning your Broccoli AI integration. This usually takes a
              few seconds.
            </p>
            <div class="d-flex justify-content-center">
              <Spinner size="medium" />
            </div>
          </template>
          <template v-else-if="provisioned">
            <p class="mb-3">
              Your API key for Broccoli AI has been provisioned. The API key was
              already revealed — use Regenerate to rotate it if you have lost
              it.
            </p>
          </template>
          <template v-else>
            <p class="mb-3">
              Click Provision to generate an API key for Broccoli AI. The key is
              shown exactly once — copy it before closing this dialog.
            </p>
          </template>
        </div>
        <template #footer>
          <div
            class="modal-footer d-flex justify-content-end align-items-center gap-2"
          >
            <template v-if="revealedKey">
              <Button data-cy="broccoli-copy-key" @click="handleCopy">
                {{ copied ? "Copied to Clipboard" : "Copy to Clipboard" }}
              </Button>
              <Button
                type="secondary"
                data-cy="broccoli-dismiss"
                @click="closeModal"
              >
                Dismiss
              </Button>
            </template>
            <Button
              v-if="showProvisionButton"
              data-cy="broccoli-provision"
              @click="handleProvision"
            >
              {{ provisioningError ? "Try Again" : "Provision" }}
            </Button>
            <Button
              v-if="showRegenerateButton"
              type="secondary"
              data-cy="broccoli-regenerate"
              @click="handleRegenerate"
            >
              {{ provisioningError ? "Try Again" : "Regenerate" }}
            </Button>
          </div>
        </template>
      </Modal>
    </template>
  </MarketplaceCard>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useStore } from "vuex";
import cable from "@/lib/cable.js";
import { EventBus } from "@/lib/event_bus";

import Button from "@/components/Button.vue";
import Modal from "@/components/Modal.vue";
import Alert from "@/components/Seranova/Alert/Alert.vue";
import InputField from "@/components/Seranova/InputField/InputField.vue";
import Spinner from "@/components/Seranova/Spinner/Spinner.vue";
import VueChannel from "@/channels/VueChannel";
import BroccoliLogo from "@/images/broccoli_logo.svg?component";
import MarketplaceCard from "./MarketplaceCard.vue";

const store = useStore();

const modalOpen = ref(false);
const copied = ref(false);
const provisioning = ref(false);
const provisioningError = ref<string | null>(null);
const revealedKey = ref<string | null>(null);
const switchOn = ref<boolean | null>(null);

let channel: VueChannel | null = null;
let copyResetTimer: ReturnType<typeof setTimeout> | null = null;

const config = computed(() => store.state.broccoliConfig);
const provisioned = computed(() => !!config.value?.aws_api_key_id);

const showProvisionButton = computed(
  () => !provisioning.value && !revealedKey.value && !provisioned.value
);
const showRegenerateButton = computed(
  () =>
    !provisioning.value &&
    !revealedKey.value &&
    provisioned.value &&
    !provisioningError.value
);

// Mirror the store into the toggle. Dispatch only when the user-driven value
// differs from the store — that filters out echoes and initial establishment
// without a stateful suppress flag (which Vue's watcher batching in tests
// couldn't reliably clear on the first mount tick).
watch(
  () => config.value?.enabled,
  (val) => {
    if (val !== undefined) switchOn.value = !!val;
  },
  { immediate: true }
);

watch(switchOn, (newValue) => {
  if (newValue === null) return;
  if (newValue === !!config.value?.enabled) return;
  if (newValue) {
    handleEnable();
  } else {
    handleDisable();
  }
});

function refreshConfig() {
  return store.dispatch("fetchBroccoliConfig").then(() => {
    if (config.value?.api_key) {
      revealedKey.value = config.value.api_key;
      modalOpen.value = true;
    }
  });
}

function openModal() {
  modalOpen.value = true;
  refreshConfig();
}

function closeModal() {
  modalOpen.value = false;
  copied.value = false;
  revealedKey.value = null;
  provisioningError.value = null;
  if (copyResetTimer) {
    clearTimeout(copyResetTimer);
    copyResetTimer = null;
  }
}

function handleEnable() {
  store.dispatch("enableBroccoli").catch((err: unknown) => {
    // Revert the toggle. The switchOn watcher gates on
    // `newValue === config.enabled`, so this revert self-cancels.
    switchOn.value = false;
    if (!provisioned.value) {
      window.notify.error(
        "Provision Broccoli AI before enabling. Use Set Up Information to get started."
      );
    } else {
      window.notify.error("Could not enable Broccoli AI.");
    }
    console.log(err);
  });
}

function handleDisable() {
  store
    .dispatch("disableBroccoli")
    .then(() => {
      window.notify.success("Broccoli AI disabled.");
      closeModal();
    })
    .catch((err: unknown) => {
      window.notify.error("Could not disable Broccoli AI.");
      console.log(err);
    });
}

function handleProvision() {
  provisioning.value = true;
  provisioningError.value = null;
  store.dispatch("provisionBroccoli").catch((err: unknown) => {
    provisioning.value = false;
    provisioningError.value = "Request could not be submitted.";
    console.log(err);
  });
}

function handleRegenerate() {
  provisioning.value = true;
  provisioningError.value = null;
  revealedKey.value = null;
  copied.value = false;
  store.dispatch("regenerateBroccoli").catch((err: unknown) => {
    provisioning.value = false;
    provisioningError.value = "Request could not be submitted.";
    console.log(err);
  });
}

function handleCopy() {
  if (!revealedKey.value) return;
  navigator.clipboard.writeText(revealedKey.value).then(() => {
    copied.value = true;
    if (copyResetTimer) clearTimeout(copyResetTimer);
    copyResetTimer = setTimeout(() => {
      copied.value = false;
      copyResetTimer = null;
    }, 2000);
  });
}

function handleProvisioned() {
  provisioning.value = false;
  provisioningError.value = null;
  refreshConfig().then(() => {
    if (revealedKey.value) window.notify.success("Broccoli AI provisioned.");
  });
}

function handleProvisioningFailed(payload?: { error?: string }) {
  provisioning.value = false;
  const detail = payload?.error || "Please try again.";
  provisioningError.value = detail;
  // Fallback toast only when the modal isn't open to show the inline alert —
  // avoids duplicate error surfaces when the admin is watching the modal.
  if (!modalOpen.value) {
    window.notify.error(`Broccoli AI provisioning failed: ${detail}`);
  }
}

onMounted(() => {
  refreshConfig();

  channel = new VueChannel();
  cable.subscribe(channel).catch((err: unknown) => {
    console.log("Unable to subscribe to VueChannel", err);
  });
  EventBus.$on("broccoli-provisioned", handleProvisioned);
  EventBus.$on("broccoli-provisioning-failed", handleProvisioningFailed);
});

onBeforeUnmount(() => {
  EventBus.$off("broccoli-provisioned", handleProvisioned);
  EventBus.$off("broccoli-provisioning-failed", handleProvisioningFailed);
  channel?.disconnect();
  if (copyResetTimer) clearTimeout(copyResetTimer);
});

// Marketplace.vue's `?card=<ref>` deep-link retry loop calls
// $refs[card].openModal(). Composition-API components hide their internal
// bindings by default, so expose openModal explicitly.
defineExpose({ openModal });
</script>
